import React from 'react';
import {ParsedApiError, shouldDisplayErrorId} from '../../../../lib/error-handler';

interface ErrorDisplayProps {
    error: ParsedApiError | string | null;
    title?: string;
    /**
     * Full-width mode for large containers like maps
     * Removes width constraints and expands to fill available space
     */
    fullWidth?: boolean;
}

/**
 * Reusable error display component for Network Optimization module
 *
 * Displays a centered error message with optional error ID for support tracking
 * Used across tables, reports, and data loading components
 */
const ErrorDisplay: React.FC<ErrorDisplayProps> = ({error, title = 'Unable to Load Data', fullWidth = false}) => {
    if (!error) return null;

    const errorMessage = typeof error === 'string' ? error : error.message;
    const errorId = typeof error === 'object' && error !== null ? error.errorId : undefined;

    return (
        <div className={`bg-white flex items-center justify-center ${fullWidth ? 'w-full h-full p-8' : 'p-12 rounded-lg min-h-[400px]'}`}>
            <div className={`text-center ${fullWidth ? 'max-w-2xl' : 'max-w-md'}`}>
                <h3 className='text-xl font-semibold text-gray-800 mb-4'>
                    {title}
                </h3>
                <p className='text-base text-gray-600 leading-relaxed mb-3'>
                    {errorMessage}
                </p>
                {errorId && shouldDisplayErrorId(errorId) && (
                    <p className='text-sm text-gray-500 font-mono bg-gray-100 px-3 py-2 rounded inline-block'>
                        Error ID: {errorId}
                    </p>
                )}
            </div>
        </div>
    );
};

export default ErrorDisplay;
